import { useState, useEffect } from 'react'
import { User } from '@shared/types'
import { UserService } from '@/services/userService'
import { getErrorMessage } from '@/utils/errorHandling'
import { useToast } from '@/hooks/useToast'

interface PaginationState {
  total: number
  pages: number
  current: number
  hasNext: boolean
  hasPrev: boolean
}

interface UseUserManagementReturn {
  // Data
  users: User[]
  pagination: PaginationState
  
  // UI State
  loading: boolean
  error: string | null
  
  // Filter State
  searchTerm: string
  roleFilter: string
  currentPage: number
  
  // Modal State
  showCreateModal: boolean
  editingUser: User | null
  deleteConfirm: User | null
  
  // Actions
  setSearchTerm: (term: string) => void
  setRoleFilter: (role: string) => void
  setCurrentPage: (page: number) => void
  setError: (error: string | null) => void
  
  // Modal Actions
  openCreateModal: () => void
  closeCreateModal: () => void
  openEditModal: (user: User) => void
  closeEditModal: () => void
  openDeleteModal: (user: User) => void
  closeDeleteModal: () => void
  
  // Data Actions
  fetchUsers: () => Promise<void>
  refreshUsers: () => Promise<void>
  deleteUser: (user: User) => Promise<void>
}

export const useUserManagement = (): UseUserManagementReturn => {
  // Data State
  const [users, setUsers] = useState<User[]>([])
  const [pagination, setPagination] = useState<PaginationState>({
    total: 0,
    pages: 0,
    current: 1,
    hasNext: false,
    hasPrev: false
  })
  
  // UI State
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  
  // Filter State
  const [searchTerm, setSearchTerm] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('')
  const [currentPage, setCurrentPage] = useState(1)
  
  // Modal State
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<User | null>(null)
  
  const { toast } = useToast()
  const pageSize = 10
  
  // Fetch users with current filters and pagination
  const fetchUsers = async () => {
    try {
      setLoading(true)
      setError(null)

      const response = await UserService.getUsersPaginated(currentPage, pageSize)
      setUsers(response.users)
      setPagination(response.pagination)
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to fetch users')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Search users
  const searchUsers = async () => {
    if (!searchTerm.trim()) {
      fetchUsers()
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await UserService.searchUsers(searchTerm, pageSize)
      setUsers(response.users)
      setPagination({
        total: response.users.length,
        pages: 1,
        current: 1,
        hasNext: false,
        hasPrev: false
      })
      setCurrentPage(1)
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to search users')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Filter users by role
  const filterByRole = async () => {
    if (!roleFilter) {
      fetchUsers()
      return
    }

    try {
      setLoading(true)
      setError(null)

      const response = await UserService.getUsersByRole(roleFilter as User['role'])
      setUsers(response.users)
      setPagination({
        total: response.users.length,
        pages: 1,
        current: 1,
        hasNext: false,
        hasPrev: false
      })
      setCurrentPage(1)
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to filter users')
      setError(errorMessage)
    } finally {
      setLoading(false)
    }
  }

  // Delete user
  const deleteUser = async (user: User) => {
    try {
      setError(null)
      await UserService.deleteUser(user.id)
      setDeleteConfirm(null)
      fetchUsers() // Refresh list
      toast.success('User deleted successfully', `${user.firstName} ${user.lastName} has been removed`)
    } catch (err) {
      const errorMessage = getErrorMessage(err, 'Failed to delete user')
      setError(errorMessage)
      toast.error('Delete failed', errorMessage)
    }
  }
  
  // Modal Actions
  const openCreateModal = () => {
    setShowCreateModal(true)
    setError(null)
  }
  
  const closeCreateModal = () => {
    setShowCreateModal(false)
    setError(null)
  }
  
  const openEditModal = (user: User) => {
    setEditingUser(user)
    setError(null)
  }
  
  const closeEditModal = () => {
    setEditingUser(null)
    setError(null)
  }
  
  const openDeleteModal = (user: User) => {
    setDeleteConfirm(user)
  }
  
  const closeDeleteModal = () => {
    setDeleteConfirm(null)
  }
  
  const refreshUsers = fetchUsers

  // Load users on component mount and when filters change
  useEffect(() => {
    if (searchTerm) {
      // Use search if there's a search term
      const timeoutId = setTimeout(() => {
        searchUsers()
      }, 300)
      return () => clearTimeout(timeoutId)
    } else if (roleFilter) {
      // Use role filter if there's a role filter but no search
      filterByRole()
    } else {
      // Default fetch with pagination
      fetchUsers()
    }
  }, [currentPage, searchTerm, roleFilter])

  return {
    // Data
    users,
    pagination,
    
    // UI State
    loading,
    error,
    
    // Filter State
    searchTerm,
    roleFilter,
    currentPage,
    
    // Modal State
    showCreateModal,
    editingUser,
    deleteConfirm,
    
    // Actions
    setSearchTerm,
    setRoleFilter,
    setCurrentPage,
    setError,
    
    // Modal Actions
    openCreateModal,
    closeCreateModal,
    openEditModal,
    closeEditModal,
    openDeleteModal,
    closeDeleteModal,
    
    // Data Actions
    fetchUsers,
    refreshUsers,
    deleteUser
  }
} 